import { Identifiable } from '@approvalmax/types';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useCallback, useMemo, useState } from 'react';

import {
    changeQbooksAccountLineItemChecked,
    changeQbooksAccountLineItemsAccount,
    changeQbooksAccountLineItemsClass,
    changeQbooksAccountLineItemsCustomer,
    changeQbooksAccountLineItemsTaxCode,
} from '../../../../actions';
import { ItemForEdit } from '../../../../components';
import { getQBooksContext } from '../../../../selectors/qbooks';
import { ExpandedQBooksAccountLineItem } from '../../../../selectors/types/ExpandedQBooksAccountLineItem';
import { useIsQBooksAccountReadonly } from '../../(legacy)/QBooksAccountLineItemsSection/hooks';
import { QBooksLineItemCustomerCell } from '../../QBooksLineItemCustomerCell/QBooksLineItemCustomerCell';
import { QbooksLineItemDropdownCell } from '../../QbooksLineItemDropdownCell/QbooksLineItemDropdownCell';
import { messages } from '../QBooksAccountLineItemsSection.messages';
import { useRequest } from './useRequest';
import { useTaxCodes } from './useTaxCodes';

/**
 * @deprecated Should be removed after AM-20441
 *
 * TODO: Remove after AM-20441
 */
export const useGroupEditItems = (accountLineItems: ExpandedQBooksAccountLineItem[]) => {
    const dispatch = useDispatch();
    const request = useRequest();
    const context = useSelector(getQBooksContext);

    const withTax = request.details.lineAmountType !== domain.LineAmountType.NoTax;
    const withClass = context.hasClassFeature;
    const withCustomer = context.hasCustomerFeature;
    const withAccount = !useIsQBooksAccountReadonly();

    const taxCodesList = useTaxCodes();

    const [isBulkEditPopupVisible, setIsBulkEditPopupVisible] = useState(false);
    const selectedItems = useMemo(() => accountLineItems.filter((item) => item.checked), [accountLineItems]);
    const qbooksContext = useSelector(getQBooksContext);

    const handleBulkEditPopupOpen = useCallback(() => {
        setIsBulkEditPopupVisible(true);
    }, []);

    const handleBulkEditPopupClose = useCallback(() => {
        setIsBulkEditPopupVisible(false);

        selectedItems.forEach((lineItem) => dispatch(changeQbooksAccountLineItemChecked(lineItem.id, false)));
    }, [dispatch, selectedItems]);

    const groupItems = useMemo(() => {
        const res: ItemForEdit<Identifiable>[] = [];

        if (withAccount) {
            res.push({
                id: 'account',
                name: messages.colNameAccount,
                render: ({ value, onChange }) => (
                    <QbooksLineItemDropdownCell
                        items={qbooksContext.accounts}
                        value={value}
                        onChange={onChange}
                        placeholder={messages.cellPlaceholderAccount}
                    />
                ),
                onSave: (newItem: domain.QBooksAccount) => {
                    dispatch(
                        changeQbooksAccountLineItemsAccount(
                            selectedItems.map((accountLineItem) => accountLineItem.id),
                            newItem
                        )
                    );
                },
            });
        }

        if (withTax) {
            res.push({
                id: 'taxRate',
                name: messages.colNameTaxCode,
                onSave: (newTax: domain.QBooksTaxCode) => {
                    dispatch(
                        changeQbooksAccountLineItemsTaxCode(
                            selectedItems.map((accountLineItem) => accountLineItem.id),
                            newTax
                        )
                    );
                },
                render: ({ value, onChange }) => {
                    return (
                        <QbooksLineItemDropdownCell
                            items={taxCodesList}
                            value={value}
                            onChange={onChange}
                            placeholder={messages.cellPlaceholderTaxCode}
                        />
                    );
                },
            });
        }

        if (withClass) {
            res.push({
                id: 'classes',
                name: messages.colNameClass,
                onSave: (newTax: domain.QBooksTaxCode) => {
                    dispatch(
                        changeQbooksAccountLineItemsClass(
                            selectedItems.map((accountLineItem) => accountLineItem.id),
                            newTax
                        )
                    );
                },
                render: ({ value, onChange }) => {
                    return (
                        <QbooksLineItemDropdownCell
                            items={qbooksContext.classes}
                            value={value}
                            onChange={onChange}
                            placeholder={messages.cellPlaceholderClass}
                        />
                    );
                },
            });
        }

        if (withCustomer) {
            res.push({
                id: 'customer',
                name: messages.colNameCustomer,
                onSave: (newTax: domain.QBooksTaxCode) => {
                    dispatch(
                        changeQbooksAccountLineItemsCustomer(
                            selectedItems.map((accountLineItem) => accountLineItem.id),
                            newTax
                        )
                    );
                },
                render: ({ value, onChange }) => {
                    return (
                        <QBooksLineItemCustomerCell
                            value={value}
                            onChange={onChange}
                            placeholder={messages.cellPlaceholderCustomer}
                        />
                    );
                },
            });
        }

        return res;
    }, [
        withAccount,
        withClass,
        withCustomer,
        dispatch,
        qbooksContext.accounts,
        qbooksContext.classes,
        selectedItems,
        taxCodesList,
        withTax,
    ]);

    return {
        groupItems,
        isBulkEditPopupVisible,
        handleBulkEditPopupOpen,
        handleBulkEditPopupClose,
    };
};
